import { css } from '@emotion/react';
import { useContext, useState } from 'react';
import moment from 'moment';
import t from 'react-translate';
import { useAppDispatch } from 'redux/store';
import MentoringProgramContext from 'athena/components/mentoring-program/context';
import { overlayStyles } from 'athena/components/mentoring-program/program-configuration/program-setup/styles';
import NvPopover from 'shared/components/nv-popover';
import NvConfirmationPopover from 'shared/components/nv-confirmation-popover';
import NvSwitch from 'shared/components/inputs/nv-switch';
import { NvTooltip } from 'shared/components/nv-tooltip';
import { SaveStatus } from 'redux/schemas/app/mentoring-programs';
import { setSaveStatus, updateMentoringProgram } from 'redux/actions/mentoring-programs';
import { wrapThunkAction } from 'redux/utils';
import { config } from '@config/pendo.config.json';


const PulseChecksSettings = () => {
  const dispatch = useAppDispatch();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { mentoringProgram } = useContext(MentoringProgramContext);
  const { pulseChecksEnabled, releasedDate } = mentoringProgram || {};

  const label = pulseChecksEnabled
    ? t.MENTORING_PROGRAMS.SETTINGS.TABS.FEEDBACK_SETTINGS.ARIA_LABEL.TURN_PULSE_CHECKS_OFF()
    : t.MENTORING_PROGRAMS.SETTINGS.TABS.FEEDBACK_SETTINGS.ARIA_LABEL.TURN_PULSE_CHECKS_ON();

  const handleConfirm = () => {
    dispatch(setSaveStatus({ newStatus: SaveStatus.IN_PROGRESS }));
    wrapThunkAction(
      dispatch(
        updateMentoringProgram({
          id: mentoringProgram.id,
          autoMatchingEnabled: !!mentoringProgram.autoMatchingEnabled,
          maxMentees: mentoringProgram.concurrentMentorConnection,
          pulseChecksEnabled: !pulseChecksEnabled,
        }),
      ),
    )
      .then(() => {
        dispatch(setSaveStatus({ newStatus: SaveStatus.COMPLETED }));
      })
      .finally(() => {
        setTimeout(() => {
          dispatch(setSaveStatus({ newStatus: null }));
        }, 3000);
        setShowConfirmation(false);
      });
  };

  const handleChange = () => {
    if (pulseChecksEnabled && moment().isAfter(releasedDate)) {
      setShowConfirmation(true);
    } else {
      handleConfirm();
    }
  };

  return (
    <div className='mb-2'>
      <div className='d-flex mb-2 align-items-center'>
        <div className='heading-5 mr-2'>{t.MENTORING_PROGRAMS.SETTINGS.TABS.FEEDBACK_SETTINGS.SESSION_PULSE_CHECKS()}</div>
        <NvPopover
          enabled
          show={showConfirmation}
          overlayStyles={overlayStyles}
          content={(
            <NvConfirmationPopover
              onCancel={() => setShowConfirmation(false)}
              onConfirm={handleConfirm}
              header={t.MENTORING_PROGRAMS.SETTINGS.TABS.FEEDBACK_SETTINGS.WARNING.TITLE()}
              message={t.MENTORING_PROGRAMS.SETTINGS.TABS.FEEDBACK_SETTINGS.WARNING.CONTENT()}
              cancelBtnText={t.FORM.CANCEL()}
              confirmBtnText={t.FORM.OK()}
            />
          )}
          rootClose
          onHide={() => setShowConfirmation(false)}
          className='d-inline-flex mw-100'
          preventOverflow
        >
          <NvTooltip preventOverflow={false} text={label}>
            <NvSwitch
              checked={pulseChecksEnabled}
              onChange={handleChange}
              enabledLabel={t.SHARED.ON().toUpperCase()}
              disabledLabel={t.SHARED.OFF().toUpperCase()}
              dataQa={config.pendo.athena.mentorshipProgram.settings.feedback.enabledSwitch}
              aria-label={label}
            />
          </NvTooltip>
        </NvPopover>
      </div>
      <div className='description text-regular'>{t.MENTORING_PROGRAMS.SETTINGS.TABS.FEEDBACK_SETTINGS.SESSION_PULSE_CHECKS_DESCRIPTION()}</div>
    </div>
  );
};

export default PulseChecksSettings;
